import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table'
import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import type { TeamMember } from '~shared/types'
import { UserRole } from '~shared/types'
import {
  useTeam,
  useTeamMembersMetadata,
  type TeamMemberMetaDto,
  useRemoveTeamMember,
  useResendInvite,
} from '@/entities/team'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/shared/ui'
import { MemberRoleCell } from './ui/member-role-cell'
import { useUser, useDashboardPermission } from '@/entities/user'
import { TeamMemberEventsSelect } from '../team-member-events-select'
import { usePendingMembersTable } from './lib/use-pending-members-table'
import { Menu, MenuItem } from '../../components/interactives/dropdown-menu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan, faEllipsis } from '@fortawesome/free-solid-svg-icons'
import { cn } from '@/utils'
import { useSetAtom } from 'jotai'
import { toastAtom } from '../../store/toast.store'
import { CreatedAtCell } from './ui/created-at-cell'

const columnHelper = createColumnHelper<TeamMember & TeamMemberMetaDto>()

type TeamTableProps = {
  teamId: string
}

export const TeamTable: FC<TeamTableProps> = ({ teamId }) => {
  const addToast = useSetAtom(toastAtom)
  const { data: user } = useUser()
  const { data: team } = useTeam({
    params: {
      teamId: teamId as string,
    },
  })
  const { data: activeTeamWithMeta } = useTeamMembersMetadata({
    params: {
      teamId: teamId as string,
    },
  })
  const { mutate: removeTeamMember, isPending: isRemovingTeamMember } =
    useRemoveTeamMember()
  const { mutate: resendInvite, isPending: isResendingInvite } =
    useResendInvite()

  const isTeamAdmin = useDashboardPermission({
    role: UserRole.TEAM_ADMIN,
  })

  const teamMembers: (TeamMember & TeamMemberMetaDto)[] = useMemo(() => {
    if (!team || !activeTeamWithMeta) return []

    return Object.entries(team?.members).map(([id, member]) => {
      return {
        ...member,
        ...activeTeamWithMeta[id],
      }
    })
  }, [activeTeamWithMeta, team])

  const handleResendInvite = useCallback(
    (email: string) => {
      resendInvite(
        {
          teamId,
          payload: {
            email,
          },
        },
        {
          onSuccess() {
            addToast({
              title: `Invite has been sent to ${email}`,
              type: 'success',
            })
          },
          onError() {
            addToast({
              title: `We were unable to send the team invite email. Please try again`,
              type: 'fail',
            })
          },
        },
      )
    },
    [addToast, resendInvite, teamId],
  )

  const handleRemoveTeamMember = useCallback(
    (uidOrEmail: string) => {
      removeTeamMember({
        teamId,
        uidOrEmail,
      })
    },
    [removeTeamMember, teamId],
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        id: 'name',
        header: 'Email',
      }),
      columnHelper.accessor('role', {
        id: 'role',
        header: 'Role',
        cell: (props) => {
          return (
            <MemberRoleCell
              context={props}
              teamId={teamId}
            />
          )
        },
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Added',
        cell: (props) => {
          return <CreatedAtCell value={props.getValue()} />
        },
      }),
      columnHelper.accessor('eventIds', {
        id: 'events',
        header: 'Events',
        cell: ({ row: { original } }) => {
          const isCurrentUser = original.email === user?.email

          if (isCurrentUser) return '-'

          if (
            [
              UserRole.EVENT_EDITOR,
              UserRole.EVENT_SHOWCALLER,
              UserRole.EVENT_VIEWER,
            ].includes(original.role)
          ) {
            return (
              <TeamMemberEventsSelect
                teamId={teamId}
                member={original}
              />
            )
          }

          return '-'
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row: { original } }) => {
          const isCurrentUser = original.email === user?.email

          return (
            <Menu
              className={
                'px-2 py-1 text-xs rounded-sm disabled:pointer-events-none ring ring-gray-800'
              }
              label={<FontAwesomeIcon icon={faEllipsis} />}
            >
              <MenuItem
                icon={faTrashCan}
                label="Remove team member"
                onClick={() => handleRemoveTeamMember(original.uid)}
                disabled={!isTeamAdmin || isCurrentUser}
              />
            </Menu>
          )
        },
      }),
    ],
    [handleRemoveTeamMember, isTeamAdmin, teamId, user?.email],
  )

  const data = useMemo(() => teamMembers ?? [], [teamMembers])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  const { rows } = table.getRowModel()
  const { pendingMembersRows } = usePendingMembersTable({
    teamId,
    onRevokeInvite: handleRemoveTeamMember,
    onResendInvite: handleResendInvite,
  })

  return (
    <Table
      className={cn(
        'relative w-full border-collapse',
        (isRemovingTeamMember || isResendingInvite) &&
          'bg-animated from-gray-900 to-gray-800',
      )}
    >
      <TableHeader className="sticky top-0 bg-gray-900">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow
            key={headerGroup.id}
            className="hover:bg-transparent"
          >
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className="text-white align-top py-2 font-semibold"
                style={{ width: header.column.getSize() }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          return (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: `${cell.column.getSize()}px`,
                    minWidth: `${cell.column.getSize()}px`,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
        {pendingMembersRows.map((row) => {
          return (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: `${cell.column.getSize()}px`,
                    minWidth: `${cell.column.getSize()}px`,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
